import React, { useState } from "react";
import { Link } from "react-router-dom";
import { BedDouble, Sparkles, PartyPopper, Compass, ArrowRight, Plus, Minus } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import OptimizedImage from "@/components/OptimizedImage";

const pick = (obj, lang) => {
  if (!obj) return "";
  if (typeof obj === "string") return obj;
  return obj[lang] || obj.en || obj.fr || "";
};

// Discovery section sits directly below the hero on the homepage.
// Acts as a visual gateway to the four primary corners of the site —
// accommodation, experiences, events and tailor-made journeys.
// Each card is collapsed by default; tapping toggles its accordion
// panel which reveals a longer paragraph + the dedicated CTA.

const ITEMS = [
  {
    key: "accommodation",
    icon: BedDouble,
    label: { en: "Accommodation", fr: "Hébergement", es: "Alojamiento" },
    headline: {
      en: "Hotels, kasbahs, riads & desert camps",
      fr: "Hôtels, kasbahs, riads & camps du désert",
      es: "Hoteles, kasbahs, riads y campamentos del desierto",
    },
    body: {
      en: "From desert camps beneath the dunes of the Sahara to kasbahs, riads, mountain retreats, lakeside escapes and heritage hotels across southern Morocco — a hand-picked collection of places rooted in landscape, culture and authentic hospitality. Each property offers its own character, story and connection to the spirit of Morocco.",
      fr: "Des camps du désert au pied des dunes du Sahara aux kasbahs, riads, refuges de montagne, escales au bord des lacs et hôtels de patrimoine à travers le sud du Maroc — une collection choisie d'adresses ancrées dans le paysage, la culture et l'hospitalité authentique. Chaque adresse a son caractère, son récit et son lien au souffle du Maroc.",
      es: "De los campamentos del desierto al pie de las dunas del Sáhara a las kasbahs, riads, refugios de montaña, escapadas junto a lagos y hoteles patrimoniales por el sur de Marruecos — una colección escogida de lugares enraizados en el paisaje, la cultura y la hospitalidad auténtica. Cada propiedad ofrece su propio carácter, su historia y su conexión con el espíritu de Marruecos.",
    },
    cta: { en: "Explore Accommodation", fr: "Explorer l'hébergement", es: "Explorar el alojamiento" },
    to: "/hotels",
  },
  {
    key: "experiences",
    icon: Sparkles,
    label: { en: "Experiences", fr: "Expériences", es: "Experiencias" },
    headline: {
      en: "Cultural, desert, adventure & wellness",
      fr: "Culture, désert, aventure & bien-être",
      es: "Cultura, desierto, aventura y bienestar",
    },
    body: {
      en: "Camel caravans at sunrise, kasbah heritage walks, Berber weaving afternoons, gnaoua music evenings, dune quads, slow oases — a curated catalogue of cultural, desert, adventure and wellness experiences, each designed with the same family who has been hosting in this region for thirty years.",
      fr: "Caravanes à l'aube, marches dans les ksour, après-midi de tissage berbère, soirées gnaoua, quads dans les dunes, oasis tranquilles — un catalogue d'expériences culturelles, désert, aventure et bien-être, conçu avec la famille qui reçoit ici depuis trente ans.",
      es: "Caravanas al amanecer, paseos por las kasbahs, tardes de tejido bereber, veladas gnaoua, quads en las dunas, oasis tranquilos — un catálogo de experiencias culturales, desierto, aventura y bienestar, diseñado con la familia que recibe aquí desde hace treinta años.",
    },
    cta: { en: "Explore Experiences", fr: "Découvrir les expériences", es: "Descubrir experiencias" },
    to: "/experiences",
  },
  {
    key: "events",
    icon: PartyPopper,
    label: { en: "Events & Celebrations", fr: "Événements & Célébrations", es: "Eventos y celebraciones" },
    headline: {
      en: "Corporate, incentives, weddings & desert galas",
      fr: "Corporate, incentives, mariages & galas du désert",
      es: "Corporativos, incentivos, bodas y galas del desierto",
    },
    body: {
      en: "Annual incentives across three hotels, kasbah-set weddings, board offsites that close with a starlit dinner on the dunes, private milestone celebrations, large-scale gala evenings — our events team has shaped some of the most memorable Sahara gatherings of the last decade.",
      fr: "Incentives annuelles sur trois hôtels, mariages en kasbah, séminaires qui se clôturent par un dîner étoilé sur les dunes, célébrations privées, galas à grande échelle — notre équipe événementielle a composé certains des plus beaux rassemblements du Sahara de la décennie.",
      es: "Incentivos anuales en tres hoteles, bodas en kasbahs, jornadas corporativas que terminan con cena estrellada en las dunas, celebraciones privadas, galas a gran escala — nuestro equipo de eventos ha compuesto algunos de los encuentros más memorables del Sáhara de la última década.",
    },
    cta: { en: "Explore Events", fr: "Découvrir les événements", es: "Descubrir eventos" },
    to: "/events",
  },
  {
    key: "trips",
    icon: Compass,
    label: { en: "Tailor-Made Trips", fr: "Voyages sur-mesure", es: "Viajes a medida" },
    headline: {
      en: "Designed around your group, pace and interests",
      fr: "Conçus autour de votre groupe, votre rythme, vos passions",
      es: "Diseñados para su grupo, ritmo e intereses",
    },
    body: {
      en: "Whether you travel as a couple chasing the slowest version of the Sahara, a multi-generation family, a small private group of photographers, or a special-interest journey (architecture, music, gastronomy) — our concierges thread the route, the rhythm and the moments around you.",
      fr: "Que vous voyagiez en couple à la recherche du Sahara le plus lent, en famille intergénérationnelle, en petit groupe privé de photographes, ou pour un voyage thématique (architecture, musique, gastronomie) — nos conciergeries tissent l'itinéraire, le rythme et les moments autour de vous.",
      es: "Tanto si viajan en pareja buscando el Sáhara más lento, en familia intergeneracional, en pequeño grupo privado de fotógrafos, o un viaje temático (arquitectura, música, gastronomía) — nuestros conserjes tejen ruta, ritmo y momentos a su medida.",
    },
    cta: { en: "Plan Your Trip", fr: "Composez votre voyage", es: "Planifique su viaje" },
    to: "/trips",
  },
];

const DiscoverySection = () => {
  const { lang } = useLanguage();
  const [openKey, setOpenKey] = useState(null);

  return (
    <section
      className="relative bg-[#FDFBF7] pt-20 md:pt-28 lg:pt-32 pb-[clamp(150px,34vw,640px)] border-b border-[#2C2621]/8 overflow-hidden"
      data-testid="home-discovery"
      data-cms-scope="home.discovery"
    >
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
        {/* Centered intro */}
        <div className="max-w-3xl mx-auto text-center">
          <img
            src="https://customer-assets.emergentagent.com/job_c24999ff-c14f-412e-baf4-b11c0020fcc2/artifacts/zezo764i_Grup_Xaluca_logo.png.webp"
            alt="Xaluca"
            loading="lazy"
            decoding="async"
            className="mx-auto mb-6 md:mb-8 h-16 sm:h-20 md:h-24 w-auto select-none"
            data-no-xaluca-logo="true"
            data-testid="discovery-xaluca-logo"
            data-cms-role="logo"
          />
          <p className="overline">
            {pick(
              { en: "Start here", fr: "Commencer ici", es: "Empieza aquí" },
              lang
            )}
          </p>
          <h2 className="font-serif-x mt-4 text-4xl sm:text-5xl md:text-6xl tracking-tighter leading-[1.04] text-[#2C2621]">
            {pick(
              {
                en: "What brings you to Grup Xaluca?",
                fr: "Qu'est-ce qui vous amène à Grup Xaluca ?",
                es: "¿Qué le trae a Grup Xaluca?",
              },
              lang
            )}
          </h2>
          <div
            className="mt-6 md:mt-7 flex items-center justify-center gap-4"
            dir="rtl"
            lang="ar"
            data-testid="discovery-arabic-greeting"
          >
            <span className="hidden sm:block h-px w-10 md:w-14 bg-[#D4A373]/50" aria-hidden="true" />
            <span className="font-serif-x text-2xl sm:text-3xl md:text-[34px] leading-none text-[#D4A373] tracking-wide">
              مرحبًا بكم
            </span>
            <span className="hidden sm:block h-px w-10 md:w-14 bg-[#D4A373]/50" aria-hidden="true" />
          </div>
          <p className="mt-5 text-base md:text-lg text-[#5C5248] leading-relaxed">
            {pick(
              {
                en: "Four ways into our southern Morocco — tap any to read more, then jump straight to where you want to be.",
                fr: "Quatre portes d'entrée vers notre sud marocain — touchez l'une d'elles pour en savoir plus, puis filez là où vous voulez être.",
                es: "Cuatro entradas a nuestro sur de Marruecos — toque cualquiera para saber más y vaya directo a donde quiere estar.",
              },
              lang
            )}
          </p>
        </div>

        {/* Cards grid */}
        <div className="mt-14 md:mt-20 grid sm:grid-cols-2 gap-5 md:gap-7">
          {ITEMS.map((it) => {
            const Icon = it.icon;
            const isOpen = openKey === it.key;
            return (
              <article
                key={it.key}
                className={`relative bg-[#FDFBF7] border transition-all duration-300 overflow-hidden group ${
                  isOpen
                    ? "border-[#C16542] shadow-[0_20px_40px_-24px_rgba(193,101,66,0.35)]"
                    : "border-[#2C2621]/12 hover:border-[#D4A373]"
                }`}
                data-testid={`discovery-card-${it.key}`}
              >
                {/* Card header — always visible. Acts as the accordion toggle. */}
                <button
                  type="button"
                  onClick={() => setOpenKey(isOpen ? null : it.key)}
                  aria-expanded={isOpen}
                  aria-controls={`discovery-panel-${it.key}`}
                  className="w-full text-left p-6 md:p-8 flex items-start gap-5"
                  data-testid={`discovery-toggle-${it.key}`}
                >
                  <span
                    className={`shrink-0 w-12 h-12 md:w-14 md:h-14 rounded-full flex items-center justify-center transition-colors duration-300 ${
                      isOpen
                        ? "bg-[#C16542] text-[#FDFBF7]"
                        : "bg-[#F2EBE1] text-[#C16542] group-hover:bg-[#D4A373]/30"
                    }`}
                    aria-hidden="true"
                  >
                    <Icon className="w-5 h-5 md:w-6 md:h-6" strokeWidth={1.5} />
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] tracking-[0.3em] uppercase text-[#C16542]">
                      {pick(it.label, lang)}
                    </p>
                    <h3 className="font-serif-x mt-2 text-xl md:text-2xl leading-snug tracking-tight text-[#2C2621]">
                      {pick(it.headline, lang)}
                    </h3>
                  </div>
                  <span
                    className={`shrink-0 mt-1 w-8 h-8 rounded-full border flex items-center justify-center transition-colors duration-300 ${
                      isOpen
                        ? "border-[#C16542] text-[#C16542] bg-[#FDFBF7]"
                        : "border-[#2C2621]/25 text-[#5C5248] group-hover:border-[#D4A373] group-hover:text-[#C16542]"
                    }`}
                    aria-hidden="true"
                  >
                    {isOpen ? (
                      <Minus className="w-3.5 h-3.5" strokeWidth={1.8} />
                    ) : (
                      <Plus className="w-3.5 h-3.5" strokeWidth={1.8} />
                    )}
                  </span>
                </button>

                {/* Accordion panel — smoothly expands. */}
                <div
                  id={`discovery-panel-${it.key}`}
                  className={`grid transition-[grid-template-rows] duration-400 ease-out ${
                    isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
                  }`}
                  aria-hidden={!isOpen}
                >
                  <div className="overflow-hidden">
                    <div className="px-6 md:px-8 pb-7 md:pb-8 pt-1 md:pl-[5.5rem]">
                      <p className="text-sm md:text-base leading-relaxed text-[#5C5248]">
                        {pick(it.body, lang)}
                      </p>
                      <Link
                        to={it.to}
                        className="mt-6 inline-flex items-center gap-2 bg-[#C16542] hover:bg-[#A35133] text-[#FDFBF7] px-6 py-3 text-[11px] tracking-[0.22em] uppercase transition-colors"
                        data-testid={`discovery-cta-${it.key}`}
                      >
                        {pick(it.cta, lang)}
                        <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.7} />
                      </Link>
                    </div>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </div>

      {/* Decorative dune horizon — anchored to the bottom edge of the
          section. Spans the full width and scales with viewport so it
          feels like part of the page rather than a standalone image.
          `pointer-events-none` keeps it from intercepting clicks on
          the cards above. Transparency is preserved (PNG with alpha)
          so the dunes settle on the cream background of the section
          below. */}
      <OptimizedImage
        src="https://customer-assets.emergentagent.com/job_c24999ff-c14f-412e-baf4-b11c0020fcc2/artifacts/b57l667e_Dunes%20Background.png"
        alt=""
        aria-hidden="true"
        sizes="100vw"
        className="pointer-events-none select-none absolute inset-x-0 bottom-0 w-full h-auto z-0"
        data-testid="home-discovery-dunes"
        objectFit="cover"
        data-cms-role="dunes"
      />
    </section>
  );
};

export default DiscoverySection;
